import { ArchitectureNode, ArchitectureEdge, ComponentType, ConnectionType } from '../types';

export type CloudProvider = 'aws' | 'gcp' | 'azure' | 'generic';

interface TerraformConfig {
  provider: CloudProvider;
  resources: TerraformResource[];
}

interface TerraformResource {
  type: string;
  name: string;
  config: Record<string, any>;
}

const componentToTerraform = (
  node: ArchitectureNode,
  provider: CloudProvider,
  edges: ArchitectureEdge[]
): TerraformResource | null => {
  const { type, label, specs } = node.data;
  const resourceName = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

  switch (type) {
    case ComponentType.WEB_SERVER:
      if (provider === 'aws') {
        return {
          type: 'aws_instance',
          name: resourceName,
          config: {
            ami: 'ami-0c55b159cbfafe1f0', // Example AMI
            instance_type: specs?.cpu || 't3.medium',
            tags: { Name: label }
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_compute_instance',
          name: resourceName,
          config: {
            machine_type: specs?.cpu || 'e2-medium',
            zone: 'us-central1-a',
            boot_disk: { initialize_params: { image: 'debian-cloud/debian-11' } },
            network_interface: { network: 'default' }
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_virtual_machine',
          name: resourceName,
          config: {
            vm_size: specs?.cpu || 'Standard_B2s',
            storage_os_disk: { create_option: 'FromImage' },
            os_profile: { computer_name: resourceName }
          }
        };
      }
      break;

    case ComponentType.DATABASE:
      if (provider === 'aws') {
        return {
          type: 'aws_db_instance',
          name: resourceName,
          config: {
            engine: specs?.engine || 'postgres',
            engine_version: '15.4',
            instance_class: specs?.cpu || 'db.t3.micro',
            allocated_storage: 20,
            db_name: resourceName,
            username: 'admin',
            password: 'changeme', // Should be in variables
            tags: { Name: label }
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_sql_database_instance',
          name: resourceName,
          config: {
            database_version: specs?.engine || 'POSTGRES_15',
            region: 'us-central1',
            settings: {
              tier: specs?.cpu || 'db-f1-micro'
            }
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_postgresql_server',
          name: resourceName,
          config: {
            sku_name: 'B_Gen5_2',
            storage_mb: 51200,
            version: '11',
            administrator_login: 'admin',
            administrator_login_password: 'changeme'
          }
        };
      }
      break;

    case ComponentType.CACHE:
      if (provider === 'aws') {
        return {
          type: 'aws_elasticache_cluster',
          name: resourceName,
          config: {
            engine: 'redis',
            node_type: specs?.cpu || 'cache.t3.micro',
            num_cache_nodes: 1,
            parameter_group_name: 'default.redis7'
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_redis_instance',
          name: resourceName,
          config: {
            tier: specs?.cpu || 'BASIC',
            memory_size_gb: 1,
            region: 'us-central1'
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_redis_cache',
          name: resourceName,
          config: {
            capacity: 1,
            family: 'C',
            sku_name: 'Basic'
          }
        };
      }
      break;

    case ComponentType.LOAD_BALANCER:
      if (provider === 'aws') {
        return {
          type: 'aws_lb',
          name: resourceName,
          config: {
            load_balancer_type: 'application',
            subnets: ['subnet-12345', 'subnet-67890'], // Should be variables
            tags: { Name: label }
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_compute_backend_service',
          name: resourceName,
          config: {
            protocol: 'HTTP',
            load_balancing_scheme: 'EXTERNAL'
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_lb',
          name: resourceName,
          config: {
            sku: 'Standard',
            frontend_ip_configuration: { name: `${resourceName}-frontend` }
          }
        };
      }
      break;

    case ComponentType.STORAGE:
      if (provider === 'aws') {
        return {
          type: 'aws_s3_bucket',
          name: resourceName,
          config: {
            bucket: resourceName,
            tags: { Name: label }
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_storage_bucket',
          name: resourceName,
          config: {
            name: resourceName,
            location: 'US'
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_storage_account',
          name: resourceName,
          config: {
            account_tier: 'Standard',
            account_replication_type: 'LRS'
          }
        };
      }
      break;

    case ComponentType.FUNCTION:
      if (provider === 'aws') {
        return {
          type: 'aws_lambda_function',
          name: resourceName,
          config: {
            filename: 'lambda.zip',
            function_name: resourceName,
            role: 'arn:aws:iam::123456789012:role/lambda-role',
            handler: 'index.handler',
            runtime: 'nodejs18.x'
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_cloudfunctions_function',
          name: resourceName,
          config: {
            name: resourceName,
            runtime: 'nodejs18',
            available_memory_mb: 256,
            source_archive_bucket: 'my-bucket',
            source_archive_object: 'function.zip',
            trigger: { http_trigger: {} }
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_function_app',
          name: resourceName,
          config: {
            app_service_plan_id: 'plan-id',
            storage_account_name: 'storage',
            storage_account_access_key: 'key'
          }
        };
      }
      break;

    case ComponentType.MICROSERVICE:
    case ComponentType.WORKER:
      // Treat as container service
      if (provider === 'aws') {
        return {
          type: 'aws_ecs_service',
          name: resourceName,
          config: {
            name: resourceName,
            cluster: 'my-cluster',
            task_definition: `${resourceName}_task`,
            desired_count: 1
          }
        };
      } else if (provider === 'gcp') {
        return {
          type: 'google_cloud_run_service',
          name: resourceName,
          config: {
            name: resourceName,
            location: 'us-central1',
            template: {
              spec: {
                containers: [{
                  image: 'gcr.io/cloudrun/hello'
                }]
              }
            }
          }
        };
      } else if (provider === 'azure') {
        return {
          type: 'azurerm_container_group',
          name: resourceName,
          config: {
            os_type: 'Linux',
            container: [{
              name: resourceName,
              image: 'mcr.microsoft.com/azuredocs/aci-helloworld',
              cpu: 0.5,
              memory: 1.5
            }]
          }
        };
      }
      break;

    default:
      // Generic resource for unknown types
      return {
        type: 'null_resource',
        name: resourceName,
        config: {
          triggers: { component_type: type, label: label }
        }
      };
  }

  return null;
};

// Helper to recursively process nodes and their nested architectures
const processNodesRecursively = (
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  provider: CloudProvider,
  resources: TerraformResource[],
  parentPath: string = ''
): void => {
  nodes.forEach(node => {
    const resource = componentToTerraform(node, provider, edges);
    if (resource) {
      // Add parent path to resource name if nested
      if (parentPath) {
        resource.name = `${parentPath}_${resource.name}`;
        if (resource.config.tags) {
          resource.config.tags.Parent = parentPath;
        } else {
          resource.config.tags = { Parent: parentPath };
        }
      }
      resources.push(resource);
    }
    
    // Recursively process nested architecture
    if (node.data.nestedArchitecture) {
      const nestedPath = parentPath ? `${parentPath}_${node.data.label.toLowerCase().replace(/\s+/g, '_')}` : node.data.label.toLowerCase().replace(/\s+/g, '_');
      processNodesRecursively(
        node.data.nestedArchitecture.nodes,
        node.data.nestedArchitecture.edges,
        provider,
        resources,
        nestedPath
      );
    }
  });
};

export const generateTerraform = (
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  provider: CloudProvider = 'aws'
): string => {
  const resources: TerraformResource[] = [];
  
  // Convert each node to a Terraform resource (recursively)
  processNodesRecursively(nodes, edges, provider, resources);

  // Generate Terraform HCL
  let output = `# Terraform configuration generated from ArchWeaver\n`;
  output += `# Provider: ${provider.toUpperCase()}\n`;
  output += `# Note: Nested components are flattened with parent prefixes\n\n`;

  // Provider configuration
  if (provider === 'aws') {
    output += `terraform {\n`;
    output += `  required_providers {\n`;
    output += `    aws = {\n`;
    output += `      source  = "hashicorp/aws"\n`;
    output += `      version = "~> 5.0"\n`;
    output += `    }\n`;
    output += `  }\n`;
    output += `}\n\n`;
    output += `provider "aws" {\n`;
    output += `  region = var.aws_region\n`;
    output += `}\n\n`;
  } else if (provider === 'gcp') {
    output += `terraform {\n`;
    output += `  required_providers {\n`;
    output += `    google = {\n`;
    output += `      source  = "hashicorp/google"\n`;
    output += `      version = "~> 5.0"\n`;
    output += `    }\n`;
    output += `  }\n`;
    output += `}\n\n`;
    output += `provider "google" {\n`;
    output += `  project = var.gcp_project\n`;
    output += `  region  = var.gcp_region\n`;
    output += `}\n\n`;
  } else if (provider === 'azure') {
    output += `terraform {\n`;
    output += `  required_providers {\n`;
    output += `    azurerm = {\n`;
    output += `      source  = "hashicorp/azurerm"\n`;
    output += `      version = "~> 3.0"\n`;
    output += `    }\n`;
    output += `  }\n`;
    output += `}\n\n`;
    output += `provider "azurerm" {\n`;
    output += `  features {}\n`;
    output += `}\n\n`;
  }

  // Variables
  if (provider === 'aws') {
    output += `variable "aws_region" {\n`;
    output += `  description = "AWS region"\n`;
    output += `  type        = string\n`;
    output += `  default     = "us-east-1"\n`;
    output += `}\n\n`;
  } else if (provider === 'gcp') {
    output += `variable "gcp_project" {\n`;
    output += `  description = "GCP project ID"\n`;
    output += `  type        = string\n`;
    output += `}\n\n`;
    output += `variable "gcp_region" {\n`;
    output += `  description = "GCP region"\n`;
    output += `  type        = string\n`;
    output += `  default     = "us-central1"\n`;
    output += `}\n\n`;
  }

  // Resources
  resources.forEach(resource => {
    output += `resource "${resource.type}" "${resource.name}" {\n`;
    Object.entries(resource.config).forEach(([key, value]) => {
      if (typeof value === 'object' && !Array.isArray(value)) {
        output += `  ${key} {\n`;
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (typeof subValue === 'object' && !Array.isArray(subValue)) {
            output += `    ${subKey} {\n`;
            Object.entries(subValue).forEach(([subSubKey, subSubValue]) => {
              output += `      ${subSubKey} = ${JSON.stringify(subSubValue)}\n`;
            });
            output += `    }\n`;
          } else {
            output += `    ${subKey} = ${JSON.stringify(subValue)}\n`;
          }
        });
        output += `  }\n`;
      } else if (Array.isArray(value)) {
        output += `  ${key} = ${JSON.stringify(value)}\n`;
      } else {
        output += `  ${key} = ${JSON.stringify(value)}\n`;
      }
    });
    output += `}\n\n`;
  });

  // Outputs
  output += `# Outputs\n`;
  resources.forEach(resource => {
    if (resource.type !== 'null_resource') {
      output += `output "${resource.name}_id" {\n`;
      output += `  value = ${resource.type}.${resource.name}.id\n`;
      output += `}\n\n`;
    }
  });

  return output;
};
